import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Megaphone, User, X } from 'lucide-react';
import { useActiveBanners, useDismissAnnouncement, useInboxItems } from '../hooks/api';
import { ROUTES } from '../constants/routes';

const PRIORITY_STYLES = {
  low: {
    wrap: 'bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-100',
    icon: 'text-sky-600 dark:text-sky-300',
  },
  medium: {
    wrap: 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-100',
    icon: 'text-emerald-600 dark:text-emerald-300',
  },
  high: {
    wrap: 'bg-amber-50 border-amber-200 text-amber-950 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-300',
  },
  critical: {
    wrap: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/50 dark:border-red-800 dark:text-red-100',
    icon: 'text-red-600 dark:text-red-300',
  },
};

/**
 * Compact top-of-app banners (single-line height) for announcements + personal notices.
 */
const AnnouncementBanner = ({ onHeightChange }) => {
  const { data: banners = [] } = useActiveBanners();
  const { data: inboxData } = useInboxItems({ filter: 'personal', page: 1, page_size: 5 });
  const dismissMutation = useDismissAnnouncement();
  const [dismissedLocal, setDismissedLocal] = useState(() => new Set());

  const personalBanners = useMemo(() => {
    const items = inboxData?.items || [];
    return items
      .filter((i) => i.item_type === 'personal' && !i.is_read && !i.is_expired)
      .slice(0, 3)
      .map((i) => ({
        id: `personal-${i.id}`,
        sourceId: i.id,
        title: i.title,
        message: i.message,
        priority: i.priority || 'medium',
        expires_at: i.expires_at,
        created_at: i.created_at,
        type: 'personal',
      }));
  }, [inboxData]);

  const visible = useMemo(() => {
    const announcementRows = (banners || [])
      .filter((b) => !dismissedLocal.has(b.id))
      .map((b) => ({ ...b, type: 'announcement' }));
    const personalRows = personalBanners.filter((b) => !dismissedLocal.has(b.id));
    return [...announcementRows, ...personalRows];
  }, [banners, personalBanners, dismissedLocal]);

  useEffect(() => {
    onHeightChange?.(visible.length > 0 ? visible.length * 36 : 0);
  }, [visible.length, onHeightChange]);

  if (!visible.length) return null;

  const handleDismiss = (item) => {
    setDismissedLocal((prev) => new Set(prev).add(item.id));
    if (item.type === 'announcement') {
      dismissMutation.mutate(item.id);
    }
  };

  return (
    <div
      className="w-full border-b border-secondary-200 dark:border-secondary-700"
      data-testid="announcement-banner-stack"
    >
      {visible.map((item) => {
        const style = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium;
        const TypeIcon =
          item.priority === 'critical' || item.priority === 'high'
            ? AlertTriangle
            : item.type === 'personal'
              ? User
              : Megaphone;
        const line = [item.title, item.message].filter(Boolean).join(' — ');

        return (
          <div
            key={item.id}
            data-testid={`banner-${item.id}`}
            className={`flex items-center gap-2 border-b px-3 py-1.5 lg:px-4 ${style.wrap}`}
          >
            <TypeIcon className={`h-3.5 w-3.5 flex-shrink-0 ${style.icon}`} aria-hidden="true" />
            <p className="min-w-0 flex-1 text-xs sm:text-sm truncate leading-5">
              <span className="font-medium">{item.title}</span>
              {item.message ? (
                <span className="opacity-90">
                  {' '}
                  {item.message}
                </span>
              ) : null}
            </p>
            <Link
              to={ROUTES.INBOX}
              className="hidden sm:inline text-xs font-medium underline-offset-2 hover:underline flex-shrink-0 whitespace-nowrap"
              title={line}
            >
              View in Inbox
            </Link>
            <button
              type="button"
              aria-label="Dismiss"
              data-testid={`dismiss-${item.id}`}
              className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 flex-shrink-0 opacity-70 hover:opacity-100"
              onClick={() => handleDismiss(item)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AnnouncementBanner;
